import { libraryInstanceSlug } from "./library-instance-slug.js";
import { libraryInboundFilterSubject, libraryOutboundFilterSubject } from "../event-subjects.js";
import { RIGHTS_CERTIFICATE_MAX_BYTES } from "../kernel/rights-certificate.js";
import { z } from "zod";
//#region src/library/library-events.ts
/**
* The NATS publish permissions of library events, compiled from verified rights certificates
* (2026-10-12-shared-libraries, R-026, R-030).
*
* The operator program sends every live rights certificate of an app in the NATS mint request, in
* the member that {@link natsMintRightsCertificatesSchema} bounds. core-api verifies each one against
* the pinned CP key, refuses one whose `appSlug` differs from the `appScope` it mints for, and
* compiles only the subjects of {@link libraryEventPublishSubjects} over the verified claims. The
* permissions are the union over the app's live versions. No consume permission on another app's
* stream ever results.
*
* Pure and browser-safe.
*/
/** The most rights certificates one NATS mint request carries: one per live version of the app. */
const NATS_MINT_RIGHTS_CERTIFICATES_MAX = 16;
/**
* The zod form of the `rightsCertificates` member of the NATS app mint request: 0 to
* {@link NATS_MINT_RIGHTS_CERTIFICATES_MAX} compact JWS texts of 1 to 65,536 characters, with no
* duplicate. The member is untrusted until the verifier passes each token.
*/
const natsMintRightsCertificatesSchema = z.array(z.string().min(1).max(RIGHTS_CERTIFICATE_MAX_BYTES)).max(16).refine((tokens) => new Set(tokens).size === tokens.length, { message: "duplicate rights certificate" });
/**
* The NATS publish subjects that the verified `claims` of one rights certificate allow, sorted and
* unique (R-026, R-030):
*
* - A `lev` entry with no `consumer` is the claim of a consumer: an app, or the instance of a library
*   that uses a library (R-033). For each inbound topic it
*   gives `zion.<instance>.in.<topic>.>`, where `<instance>` is
*   `libraryInstanceSlug(entry.slug, entry.id, claims.sub)`: core-api computes the instance slug
*   itself from the signed library id and the consumer's `sub`.
* - A `lev` entry with a `consumer` is the claim of an instance. For each outbound topic it gives
*   `zion.<consumer.slug>.lib.<entry.slug>.<topic>.>`.
* - A certificate with no `lev` gives no subject.
*
* Pass only claims that `RightsCertificateVerifier.verify` returned. It throws `Error` or
* `TypeError` for claims the claims schema refuses, for example a library slug with `--`. Pure.
*/
function libraryEventPublishSubjects(claims) {
	const subjects = /* @__PURE__ */ new Set();
	for (const entry of claims.lev ?? []) if (entry.consumer === void 0) {
		const instance = libraryInstanceSlug(entry.slug, entry.id, claims.sub);
		for (const topic of entry.inbound) subjects.add(libraryInboundFilterSubject(instance, topic));
	} else for (const topic of entry.outbound) subjects.add(libraryOutboundFilterSubject(entry.consumer.slug, entry.slug, topic));
	return [...subjects].sort();
}
//#endregion
export { NATS_MINT_RIGHTS_CERTIFICATES_MAX, libraryEventPublishSubjects, natsMintRightsCertificatesSchema };

//# sourceMappingURL=library-events.js.map